import { useEffect, useRef, useState } from "react";
import { BrowserMultiFormatReader } from "@zxing/browser";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

// Phone/webcam barcode scanning. Acquires the rear camera, forces iOS-safe
// inline playback, then decodes from the live video. Reports the code + closes.
export function CameraScanner({ open, onOpenChange, onDetected }) {
  const videoRef = useRef(null);
  const controlsRef = useRef(null);
  const streamRef = useRef(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open) return;
    let active = true;
    setErr("");
    const reader = new BrowserMultiFormatReader();

    const stopAll = () => {
      try { controlsRef.current?.stop(); } catch { /* noop */ }
      try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch { /* noop */ }
      controlsRef.current = null;
      streamRef.current = null;
    };

    (async () => {
      try {
        if (!navigator.mediaDevices?.getUserMedia) { setErr("Camera not supported on this browser."); return; }
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: "environment" } }, audio: false,
        });
        if (!active) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        const v = videoRef.current;
        if (!v) return;
        // iOS Safari needs these as real attributes before play()
        v.setAttribute("playsinline", "true");
        v.setAttribute("autoplay", "true");
        v.setAttribute("muted", "true");
        v.muted = true;
        v.playsInline = true;
        v.srcObject = stream;
        try { await v.play(); } catch { /* play may still start on metadata */ }
        const controls = await reader.decodeFromVideoElement(v, (result) => {
          if (result && active) {
            active = false;
            const code = result.getText();
            stopAll();
            onDetected(code);
            onOpenChange(false);
          }
        });
        controlsRef.current = controls;
      } catch (e) {
        setErr(e?.name === "NotAllowedError"
          ? "Camera access was blocked. Allow camera for this site in your browser settings."
          : "Could not start the camera. Close and try again.");
      }
    })();

    return () => { active = false; stopAll(); };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="camera-scanner-dialog">
        <DialogHeader><DialogTitle>Scan with camera</DialogTitle></DialogHeader>
        <div className="rounded-lg overflow-hidden bg-black aspect-[3/4] relative">
          <video ref={videoRef} className="h-full w-full object-cover" autoPlay muted playsInline />
          <div className="absolute inset-x-8 top-1/2 h-0.5 bg-emerald-400/90 animate-pulse" />
        </div>
        {err
          ? <p className="text-xs text-rose-600 text-center" data-testid="camera-scanner-error">{err}</p>
          : <p className="text-xs text-muted-foreground text-center">Point your camera at the pack barcode. Hold steady until it reads.</p>}
      </DialogContent>
    </Dialog>
  );
}
